import type { Types } from '@ohif/core';
import { utils } from '@ohif/core';

const { sopClassDictionary: sop } = utils;

// DisplaySet (platform/core/src/types/DisplaySet.ts) omits SOPClassUID, though
// runtime display sets from extensions/default's and -dicom-seg's handlers carry it.
type DisplaySetWithSOPClassUID = Types.DisplaySet & {
  SOPClassUID?: string;
  instances?: Array<{ SOPClassUID?: string }>;
};

const DOWNLOADABLE_SOP_CLASS_UIDS = new Set<string>([
  sop.CTImageStorage,
  sop.EnhancedCTImageStorage,
  sop.LegacyConvertedEnhancedCTImageStorage,
  sop.SecondaryCaptureImageStorage,
  sop.MultiframeSingleBitSecondaryCaptureImageStorage,
  sop.MultiframeGrayscaleByteSecondaryCaptureImageStorage,
  sop.MultiframeGrayscaleWordSecondaryCaptureImageStorage,
  sop.MultiframeTrueColorSecondaryCaptureImageStorage,
]);

export function isDownloadableDisplaySet(
  displaySetInstanceUID: unknown,
  displaySetService: AppTypes.DisplaySetService
): boolean {
  // getDisplaySetByUID throws on a non-string, and returns undefined on a cache
  // miss while declaring `: DisplaySet` -- neither guard is implied by the types.
  if (typeof displaySetInstanceUID !== 'string') {
    return false;
  }
  const displaySet = displaySetService.getDisplaySetByUID(
    displaySetInstanceUID
  ) as DisplaySetWithSOPClassUID;
  if (!displaySet) {
    return false;
  }

  // D18: only these two Modalities are literals in their handlers
  // (cornerstone-dicom-rt getSopClassHandlerModule.ts, and the SR sibling).
  // SC and CT copy the tag verbatim, so they are gated on SOP class instead.
  if (displaySet.Modality === 'RTSTRUCT' || displaySet.Modality === 'SR') {
    return true;
  }

  // D19: the instances[0] fallback catches SC that extensions/dicom-video
  // claimed -- that display set carries no SOPClassUID field at all.
  const sopClassUID = displaySet.SOPClassUID ?? displaySet.instances?.[0]?.SOPClassUID;

  return sopClassUID !== undefined && DOWNLOADABLE_SOP_CLASS_UIDS.has(sopClassUID);
}
